import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  ALL_DAYS, PLATFORMS, PLATFORM_ORDER, PLATFORM_NOTES, NICHE_OPTIONS,
  TYPE_TAGS, TYPE_LABELS, personalize, buildGenericPlatforms, getAeoBadgeClass,
} from "@/data/challengeDays";
import { clearToken } from "@/api";
import Day30Celebration from "@/components/Day30Celebration";
import ProgressReview from "@/components/ProgressReview";
import FindabilityCheck from "@/components/FindabilityCheck";
import CycleFindabilityPanel from "@/components/CycleFindabilityPanel";
import PastCycles from "@/components/PastCycles";
import AccountPanel from "@/components/AccountPanel";
import { createJobPoll } from "@/lib/jobPoll";
import { toCustomerText, extractPoints, countPrompts } from "@/lib/personalizationPoints";

const EMPTY_PROFILE = { niche: "", customNiche: "", brand: "", audience: "", audienceShort: "", searchableTitle: "", bio: "" };

function computeScore(n) {
  if (n === 0) return "—";
  if (n < 7) return "Emerging";
  if (n < 15) return "Building";
  if (n < 25) return "Visible";
  return "Improved";
}

const TABS = [
  { key: "generate", label: "✦ Generated Post" },
  { key: "calendar", label: "📅 Effort Calendar" },
  { key: "vault", label: "📁 Saved Posts" },
  { key: "history", label: "🗂️ Past Cycles" },
  { key: "findability", label: "🔎 Test Your Findability" },
  { key: "platforms", label: "🌐 Platform Guide" },
  { key: "tips", label: "🎯 AEO Tips" },
];

const STATUS_OPTS = [
  { key: "draft", label: "Draft" },
  { key: "ready", label: "Ready" },
  { key: "posted", label: "Posted" },
];
const statusLabel = (s) => (STATUS_OPTS.find((o) => o.key === s) || STATUS_OPTS[0]).label;

const PLATFORM_GUIDE = [
  { name: "🌐 Your Website", aeo: "AEO #1", cls: "aeo-strong", desc: "The only address AI trusts completely. Every post on joannelangston.com builds your permanent, indexed entity profile.", tip: "Post here first. Everything else is a signal that points back to it." },
  { name: "📝 Substack", aeo: "AEO #2", cls: "aeo-strong", desc: "Indexed by Google and treated like a real publication by AI engines. Long-form, text-rich content lives well here.", tip: "Essay voice. End with \"reply and tell me.\" No hashtags." },
  { name: "💼 LinkedIn", aeo: "AEO #3", cls: "aeo-strong", desc: "Articles carry domain authority. Best for professional credibility and reaching event organizers and collaborators.", tip: "First line must stop the scroll. Max 3-5 hashtags. End with a clear point of view." },
  { name: "📌 Pinterest", aeo: "AEO #4", cls: "aeo-strong", desc: "Fully indexed by Google. Your pin descriptions are AEO assets hiding in plain sight. Your visual work belongs here.", tip: "No hashtags. 220-232 characters. End with a CTA pointing to joannelangston.com." },
  { name: "👥 Facebook", aeo: "Human Reach", cls: "aeo-medium", desc: "A walled garden — AI cannot read inside. Build your human community and warm relationships here.", tip: "Warm and conversational. Always end with a genuine question." },
  { name: "📷 Instagram", aeo: "Human Reach", cls: "aeo-medium", desc: "Visual-first and community-building. AI cannot read inside, but your captions travel when people share and save.", tip: "Short punchy lines. 4-5 hashtags. Always include a save prompt." },
  { name: "🌿 Skool", aeo: "Community", cls: "aeo-community", desc: "AI cannot index inside Skool. This is purely for depth, trust, and engagement with your Oracle Connections community.", tip: "Reference the workshop. End with a participation prompt. Make people feel seen." },
];

const AEO_TIPS = [
  { icon: "❓", title: "Start with the Question", desc: "Open every post with the exact question your audience types. Answer it immediately. AI scans for content that directly matches a query." },
  { icon: "📋", title: "Use Steps and Lists", desc: "Numbered lists and step-by-step formats are the most citable by AI. Structure your expertise as ordered steps whenever you can." },
  { icon: "🏷️", title: "Define Your Terms", desc: "When you define concepts with authority, AI pulls that definition into answers. Creative provenance. The story moment. Define your world." },
  { icon: "🗣️", title: "Name Yourself Consistently", desc: "Repeat your Searchable Title everywhere. \"[Name] is a [title] who helps [audience]\" — AI builds an entity profile from repeated signals." },
  { icon: "🌵", title: "Your Story IS the Strategy", desc: "Your Creative Provenance — where this work came from — is not just beautiful. It's findable. Origin stories help AI distinguish real experts." },
  { icon: "📊", title: "Be Specific", desc: "Vague content gets skipped. \"Most people\" becomes \"8 of the 10 people I work with.\" Specific details and outcomes make you more citable." },
  { icon: "🔄", title: "Post First on Your Website", desc: "Every piece of content should live on joannelangston.com first. Then adapt it for each platform. Your website is your AEO foundation." },
  { icon: "🔗", title: "Every Post Points Home", desc: "Every caption, every bio, every post should point to joannelangston.com. AI tools weight entities that have a real web home." },
];

export default function Challenge({ user, setUser, source, cycleStatus, cycle }) {
  const cycleMode = source && source.mode === "cycle";
  const [form, setForm] = useState({ ...EMPTY_PROFILE, ...((source && source.focus) || user.profile || {}) });
  // Cycle mode must NEVER seed progress from the legacy GLOBAL user.completed (that would leak a prior
  // cycle's completion into a fresh cycle). Start empty and let the cycle-scoped load populate it; a
  // failed load then safely shows 0% rather than a stale 100%.
  const [completedState, setCompletedState] = useState(cycleMode ? [] : (user.completed || []));
  const [vaultState, setVaultState] = useState(user.vault || []);
  const [day, setDay] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [activeTab, setActiveTab] = useState("generate");
  const [generating, setGenerating] = useState(false);
  const [card, setCard] = useState(null); // { dayData, platforms, opts, platform }
  const [cardPlatformTab, setCardPlatformTab] = useState("website");
  const [hint, setHint] = useState("");
  const [copiedKey, setCopiedKey] = useState("");
  const [savedKey, setSavedKey] = useState("");
  const [doneFlash, setDoneFlash] = useState(false);
  const [showDay30, setShowDay30] = useState(false);
  const [cardEdits, setCardEdits] = useState({});      // platform -> edited text (pre-save)
  const [cardEditPlatform, setCardEditPlatform] = useState("");
  const [vaultFilter, setVaultFilter] = useState("all");
  const [vaultEdits, setVaultEdits] = useState({});    // id -> { text, imageRef }
  const [genQueueMsg, setGenQueueMsg] = useState("");
  const [genError, setGenError] = useState("");
  const genBusyRef = useRef(false);
  const pollRef = useRef(null);

  const completed = useMemo(() => completedState || [], [completedState]);
  const vault = vaultState || [];

  // ── Load completed efforts + saved posts from the active source (legacy user.* or cycle API) ──
  useEffect(() => {
    let cancelled = false;
    source.loadInitial().then(({ completed, vault, focus }) => {
      if (cancelled) return;
      setCompletedState(completed || []);
      setVaultState(vault || []);
      if (!source.focusEditable && focus) setForm((f) => ({ ...f, ...focus }));
    }).catch(() => {
      if (cancelled) return;
      // Fail safe: a failed cycle-scoped load must NOT fall back to legacy global completion.
      if (cycleMode) { setCompletedState([]); setVaultState([]); }
    });
    return () => { cancelled = true; };
  }, [source]);

  // ── Persist profile inputs (debounced) — LEGACY/editable focus only. Cycle focus is immutable. ──
  const firstProfile = useRef(true);
  useEffect(() => {
    if (!source.focusEditable) return;
    if (firstProfile.current) { firstProfile.current = false; return; }
    const t = setTimeout(() => {
      source.saveFocus(form).catch((e) => console.error("Failed to save profile:", e));
    }, 600);
    return () => clearTimeout(t);
  }, [form, source]);

  useEffect(() => () => { if (pollRef.current) pollRef.current.stop(); }, []);

  // The celebration modal is driven by CANONICAL completion only — never by marking Effort 30.
  // In cycle mode it opens when the lifecycle status is `completed`; the ribbon "What's next" button
  // remains an explicit user-initiated way to reopen it. (Legacy mode has no cycleStatus, so it opens
  // only via that explicit ribbon CTA shown at 30/30 — it no longer auto-opens on marking day 30.)
  useEffect(() => {
    if (cycleStatus === "completed") setShowDay30(true);
  }, [cycleStatus]);

  const setField = (k, v) => { if (!source.focusEditable) return; setForm((f) => ({ ...f, [k]: v })); };

  const getNiche = useCallback(() => {
    if (form.niche === "custom") return form.customNiche || "your creative work";
    return form.niche || "your creative work";
  }, [form.niche, form.customNiche]);

  const getOpts = useCallback(() => {
    const niche = getNiche();
    return {
      niche,
      brand: (form.brand || "").trim() || "[Your Name]",
      audience: (form.audience || "").trim() || "[your audience]",
      audienceShort: (form.audienceShort || "").trim(),
      searchableTitle: (form.searchableTitle || "").trim() || niche + " specialist",
    };
  }, [form, getNiche]);

  // ── Stats ──
  const stats = useMemo(() => {
    const n = completed.length;
    const pct = Math.round((n / 30) * 100);
    return { n, pct, score: computeScore(n) };
  }, [completed]);

  // ── Generate ──
  const finishPost = (res, dayData, opts) => {
    if (res.status === "done") {
      // Presentation layer: hide internal PP scaffolding, render human-readable fill-in prompts.
      const raw = res.platforms || {};
      const filled = {};
      const points = {};
      Object.keys(raw).forEach((p) => { filled[p] = toCustomerText(raw[p]); points[p] = extractPoints(raw[p]); });
      setCard({ dayData, opts, platform: selectedPlatform, filled, points, failedPlatforms: res.failed_platforms || [] });
      setHint(`Day ${dayData.day} note: ${dayData.hook}`);
      setGenerating(false); setGenQueueMsg(""); genBusyRef.current = false;
      return true;
    }
    if (res.status === "failed" || res.status === "busy") {
      setGenError(res.message || "The studio is busy right now — your work is saved. Try again in a moment.");
      setGenerating(false); setGenQueueMsg(""); genBusyRef.current = false;
      return true;
    }
    setGenQueueMsg(res.status === "queued" && res.position > 1
      ? `You're #${res.position} in line — your posts are moments away.`
      : "Writing your posts…");
    return false;
  };

  const pollPost = (jobId, dayData, opts) => {
    if (pollRef.current) pollRef.current.stop();
    const BUSY = "The studio is busy right now — your work is saved. Try again in a moment.";
    const stopUI = (msg) => { setGenError(msg); setGenerating(false); setGenQueueMsg(""); genBusyRef.current = false; };
    pollRef.current = createJobPoll({
      getJob: source.getGenJob,
      onProgress: ({ status, position, phase }) =>
        setGenQueueMsg(
          phase === "fast"
            ? (status === "queued" && position > 1
                ? `You're #${position} in line — your posts are moments away.`
                : "Writing your posts…")
            : "Still creating your post. You can stay here while it finishes."),
      onDone: (res) => finishPost(res, dayData, opts),
      onFailed: (res) => stopUI(res.message || BUSY),
      onExpired: () => stopUI("This request expired. Please generate again."),
      onGiveUp: () => stopUI("This request is taking longer than expected. Please try again."),
    });
    pollRef.current.start(jobId);
  };

  const runGenerate = async (plat) => {
    if (!day) { alert("Please select an effort."); return; }
    if (genBusyRef.current) return;
    const dayData = ALL_DAYS.find((d) => String(d.day) === String(day));
    if (!dayData) return;
    const opts = getOpts();
    const rawPlatforms = dayData.platforms || buildGenericPlatforms(dayData, opts);
    const fallback = buildGenericPlatforms(dayData, opts);
    const scope = plat === "all" ? PLATFORM_ORDER : [plat];
    const drafts = {};
    scope.forEach((p) => { drafts[p] = personalize(rawPlatforms[p] || fallback[p], opts); });

    genBusyRef.current = true;
    setGenerating(true);
    setGenError("");
    setGenQueueMsg("Writing your posts…");
    setCard(null);
    setCardEdits({});
    setCardEditPlatform("");
    setCardPlatformTab(scope[0]);
    try {
      if (source.focusEditable) await source.saveFocus(form).catch(() => {});  // flush latest focus (legacy only)
      const res = await source.generate({ day: dayData.day, platform: plat, drafts });
      if (!finishPost(res, dayData, opts) && res.job_id) pollPost(res.job_id, dayData, opts);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setGenError((typeof detail === "string" ? detail : detail?.message) || "Could not generate right now. Please try again.");
      setGenerating(false); setGenQueueMsg(""); genBusyRef.current = false;
    }
  };

  const generatePost = () => runGenerate(selectedPlatform);
  const retryPlatform = (p) => runGenerate(p);

  const copyText = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(""), 1800);
  };

  const doSave = async (dayData, p, text, key) => {
    const item = {
      day: dayData.day, theme: dayData.theme, type: dayData.type,
      platform: p, platformLabel: PLATFORMS[p].label, text,
    };
    try {
      const res = await source.addVault(item);
      setVaultState((v) => [res.item, ...v]);
      setSavedKey(key);
      setTimeout(() => setSavedKey(""), 1800);
    } catch (e) { console.error("Failed to save post to vault:", e); }
  };

  const markDone = async (dayNum) => {
    if (completed.includes(dayNum)) { flashDone(); return; }
    try {
      const next = await source.markDone(dayNum);
      setCompletedState(next);
      flashDone();
    } catch (e) { console.error("Failed to mark day done:", e); }
  };

  const flashDone = () => { setDoneFlash(true); setTimeout(() => setDoneFlash(false), 1800); };

  const toggleDay = async (dayNum) => {
    const isDone = completed.includes(dayNum);
    try {
      const next = await source.toggleDay(dayNum, !isDone);
      setCompletedState(next);
    } catch (e) { console.error("Failed to toggle day:", e); }
  };

  // Open an effort for drafting WITHOUT marking it complete — plain tile clicks must never write.
  const selectEffort = (dayNum) => {
    setDay(String(dayNum));
    setActiveTab("generate");
  };

  const removeVault = async (id) => {
    try {
      const res = await source.removeVault(id);
      setVaultState(res.vault);
    } catch (e) { console.error("Failed to delete vault item:", e); }
  };

  const patchVault = async (id, patch) => {
    try {
      const res = await source.patchVault(id, patch);
      setVaultState(res.vault);
    } catch (e) { console.error("Failed to update vault item:", e); }
  };

  const clearForm = () => {
    setForm({ ...EMPTY_PROFILE });
    setDay("");
    setHint("");
    setCard(null);
  };

  const signOut = () => { clearToken(); setUser(null); };
  const [showAccount, setShowAccount] = useState(false);

  // ── Render helpers for the generated card ──
  const renderCardPlatform = (p) => {
    const { dayData } = card;
    const generated = (card.filled && card.filled[p]) || "";
    const value = cardEdits[p] ?? generated;
    const isEditing = cardEditPlatform === p;
    const info = PLATFORMS[p];
    const copyKey = `copy_${p}`;
    const saveKey = `save_${p}`;
    return (
      <div key={p} className="platform-pane" data-testid={`platform-pane-${p}`}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <div className="platform-label">
            {info.label} <span className={`platform-aeo-badge ${getAeoBadgeClass(p)}`}>{info.aeo}</span>
          </div>
          <button
            className="btn-tiny"
            onClick={() => setCardEditPlatform(isEditing ? "" : p)}
            data-testid={`card-edit-toggle-${p}`}
          >
            {isEditing ? "✓ Done editing" : "✎ Edit"}
          </button>
        </div>
        <div className="post-note platform-note">{info.note}</div>
        {isEditing ? (
          <textarea
            className="post-edit-area"
            data-testid={`post-edit-${p}`}
            value={value}
            rows={12}
            onChange={(e) => setCardEdits((m) => ({ ...m, [p]: e.target.value }))}
          />
        ) : (
          <div className="post-text" data-testid={`post-text-${p}`}>{value}</div>
        )}
        {countPrompts(value) > 0 && (
          <div className="pp-needs-input" data-testid={`pp-needs-input-${p}`}
            style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8, fontSize: ".82rem",
                     background: "rgba(217,119,6,.10)", border: "1px solid rgba(217,119,6,.35)", color: "#b45309" }}>
            ✍️ {countPrompts(value)} spot{countPrompts(value) > 1 ? "s" : ""} still need your input — fill the highlighted prompt{countPrompts(value) > 1 ? "s" : ""} before posting. You can still copy or save.
          </div>
        )}
        <div className="copy-row">
          <button className="btn btn-teal" onClick={() => copyText(value, copyKey)} data-testid={`copy-button-${p}`}>
            {copiedKey === copyKey ? "Copied!" : "Copy Post"}
          </button>
          <button className="btn btn-o" onClick={() => doSave(dayData, p, value, saveKey)} data-testid={`save-button-${p}`}>
            {savedKey === saveKey ? "✓ Saved!" : "Save to Vault"}
          </button>
        </div>
      </div>
    );
  };

  const renderCard = () => {
    if (generating) {
      return (
        <div className="gen-anim" data-testid="gen-anim">
          <div className="gen-dots"><span /><span /><span /></div>
          {genQueueMsg || "Writing your posts…"}
        </div>
      );
    }
    if (!card) {
      return (
        <div className="empty-state" data-testid="generate-empty-state">
          {genError && <div className="fc-error" data-testid="generate-error" style={{ marginBottom: 16 }}>{genError}</div>}
          Fill in your details on the left and click <strong>Generate Post</strong> to begin.<br /><br />
          Choose <strong>All Platforms</strong> to get every version at once — Website, Substack, LinkedIn,
          Pinterest, Facebook, Instagram, and Skool — each fully written and ready to copy.
        </div>
      );
    }
    const { dayData, platform } = card;
    const tagClass = TYPE_TAGS[dayData.type] || "tag-ai";
    const tagLabel = TYPE_LABELS[dayData.type] || "";
    return (
      <div className="post-block" data-testid="post-card">
        <span className={`rb-tag ${tagClass}`}>{tagLabel}</span>
        <h3>Effort {dayData.day}: {dayData.theme}</h3>
        <div className="post-format">Format: {dayData.format}</div>

        {platform === "all" ? (
          <>
            {card.failedPlatforms?.length > 0 && (
              <div className="fc-error" data-testid="partial-failure-notice" style={{ marginBottom: 12 }}>
                Couldn’t generate: {card.failedPlatforms.map((f) => PLATFORMS[f.platform]?.label || f.platform).join(", ")}.
                {card.failedPlatforms.map((f) => (
                  <button key={f.platform} className="btn-tiny" style={{ marginLeft: 8 }}
                    data-testid={`retry-platform-${f.platform}`} onClick={() => retryPlatform(f.platform)}>
                    Retry {PLATFORMS[f.platform]?.label || f.platform}
                  </button>
                ))}
              </div>
            )}
            <div className="platform-tabs" role="tablist" aria-label="Platform versions">
              {PLATFORM_ORDER.map((p) => (
                <button
                  key={p}
                  role="tab"
                  aria-selected={cardPlatformTab === p}
                  className={`ptab${cardPlatformTab === p ? " active" : ""}`}
                  onClick={() => setCardPlatformTab(p)}
                  data-testid={`platform-tab-${p}`}
                >
                  {PLATFORMS[p].label}
                </button>
              ))}
            </div>
            {renderCardPlatform(cardPlatformTab)}
            <div className="copy-row copy-row-footer">
              <button className="btn btn-o" onClick={() => markDone(dayData.day)} data-testid="mark-done-all">
                {doneFlash ? "✓ Done!" : `✓ Mark Effort ${dayData.day} Done`}
              </button>
            </div>
          </>
        ) : (
          <>
            {renderCardPlatform(platform)}
            <div className="copy-row copy-row-footer">
              <button className="btn btn-o" onClick={() => markDone(dayData.day)} data-testid="mark-done-single">
                {doneFlash ? "✓ Done!" : "✓ Mark Done"}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <header className="site-header">
        <div className="header-inner">
          <div className="header-brand-wrap">
            <span style={{ fontSize: "1.4rem" }}>🌵</span>
            <div>
              <div className="header-brand">Lonesome Cactus Studio</div>
              <div className="header-sub">Findability Cycle</div>
            </div>
          </div>
          <div className="header-right">
            <span className="header-user" data-testid="header-user-email">{user.email}</span>
            <span className="badge-pill" data-testid="day-badge">Effort {stats.n} / 30</span>
            <button className="header-signout" onClick={() => setShowAccount(true)} data-testid="account-button">Account</button>
            <button className="header-signout" onClick={signOut} data-testid="signout-button">Sign out</button>
          </div>
        </div>
      </header>
      {showAccount && <AccountPanel email={user.email} onClose={() => setShowAccount(false)} onSignOut={signOut} />}

      <div className="hero">
        <div className="hero-main">
          <div className="eyebrow"><span className="eyebrow-dot" /> Lonesome Cactus Studio · Findability Cycle</div>
          <h1 className="hero-title">Get your story found<br /><em>one effort at a time.</em></h1>
          <p className="hero-desc">
            You found your words in the Findability Session. Now your Findability Cycle puts them into the world, one focused effort at a time,
            formatted for the platform you are actually posting to, in formats AI engines cite when someone searches for what you do.
          </p>
          <div className="hero-stats">
            <div className="stat"><span>Efforts Complete</span><strong data-testid="stat-days">{stats.n}</strong></div>
            <div className="stat"><span>Posts Saved</span><strong data-testid="stat-posts">{vault.length}</strong></div>
            <div className="stat"><span>Findability</span><strong data-testid="stat-score">{stats.score}</strong></div>
          </div>
          <div className="prog-wrap">
            <div className="prog-row"><span>Cycle Progress</span><span data-testid="progress-label">{stats.pct}% complete</span></div>
            <div className="prog-track"><div className="prog-fill" style={{ width: `${stats.pct}%` }} /></div>
          </div>
          {/* Final completion banner: in cycle mode it requires the CANONICAL lifecycle status
              `completed` (30/30 alone, or `ready_to_complete`, must NOT declare the cycle done).
              Legacy mode keeps its existing length-based behavior. */}
          {(cycleMode ? cycleStatus === "completed" : stats.n === 30) && (
            <div className="hero-complete-ribbon" data-testid="hero-complete-ribbon">
              <span className="rib-emoji">🌵</span>
              <span className="rib-text"><strong>Cycle complete.</strong> Your story is ready to be found — keep the momentum going.</span>
              <button className="rib-cta" onClick={() => setShowDay30(true)} data-testid="hero-complete-cta">What's next →</button>
            </div>
          )}
        </div>
      </div>

      {cycleMode && stats.n === 30 &&
        ((cycleStatus === "active" && cycle && cycle.progress_review_confirmed === false)
          || (cycle && cycle.ready_to_complete === true)) && (
        <div className="app-wrap" style={{ marginTop: "1.5rem" }} data-testid="progress-review-section">
          <ProgressReview cycle={cycle} onCompleted={() => setShowDay30(true)} />
        </div>
      )}

      <div className="app-wrap">
        <div className="app-layout">

          {/* FORM PANEL */}
          <div className="form-panel">
            <h2 className="panel-title">Generate Your Post</h2>
            <div className="panel-sub">Bring what you found in your Findability Session and choose where you are posting.</div>

            <div className="field">
              <label htmlFor="nicheSelect">Your Creative Work</label>
              <select id="nicheSelect" value={form.niche} onChange={(e) => setField("niche", e.target.value)} data-testid="niche-select">
                {form.niche && form.niche !== "custom" && !NICHE_OPTIONS.some((o) => o.value === form.niche) && (
                  <option value={form.niche}>{form.niche}</option>
                )}
                {NICHE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {form.niche === "custom" && (
              <div className="field">
                <label htmlFor="customNiche">Your Creative Work</label>
                <input id="customNiche" type="text" placeholder="e.g., watercolor portrait artist"
                  value={form.customNiche} onChange={(e) => setField("customNiche", e.target.value)} data-testid="custom-niche-input" />
              </div>
            )}
            <div className="field">
              <label htmlFor="brandName">Your Name</label>
              <input id="brandName" type="text" placeholder="e.g., Jo Langston"
                value={form.brand} onChange={(e) => setField("brand", e.target.value)} data-testid="brand-name-input" />
            </div>
            <div className="field">
              <label htmlFor="audience">Who You Help</label>
              <input id="audience" type="text" placeholder="e.g., unpublished authors whose stories live in a drawer"
                value={form.audience} onChange={(e) => setField("audience", e.target.value)} data-testid="audience-input" />
            </div>
            <div className="field">
              <label htmlFor="audienceShort">Who You Help — Short Version <span style={{ textTransform: "none", fontWeight: 400, color: "var(--muted)" }}>(optional)</span></label>
              <input id="audienceShort" type="text" placeholder="e.g., authors with a story in the drawer"
                value={form.audienceShort} onChange={(e) => setField("audienceShort", e.target.value)} data-testid="audience-short-input" />
              <div className="platform-aeo-note" style={{ marginTop: 4 }}>A crisp 3-6 word version used in tight spots (role lines, labels). Your full phrase stays in the warm "I help…" sentences.</div>
            </div>
            <div className="field">
              <label htmlFor="searchableTitle">Your Searchable Title (from Findability Session)</label>
              <input id="searchableTitle" type="text" placeholder="e.g., AI-assisted visual artist for illustrated books"
                value={form.searchableTitle} onChange={(e) => setField("searchableTitle", e.target.value)} data-testid="searchable-title-input" />
            </div>
            <div className="field">
              <label htmlFor="bio">Your AEO-Ready Bio (from Findability Session)</label>
              <textarea id="bio" placeholder="Paste your bio from the Findability Session, or describe your expertise briefly."
                value={form.bio} onChange={(e) => setField("bio", e.target.value)} data-testid="bio-input" />
            </div>
            <div className="field">
              <label htmlFor="daySelect">Effort</label>
              <select id="daySelect" value={day} onChange={(e) => setDay(e.target.value)} data-testid="day-select">
                <option value="">Select an effort…</option>
                {ALL_DAYS.map((d) => <option key={d.day} value={d.day}>Effort {d.day}: {d.theme}</option>)}
              </select>
            </div>

            <div className="divider" />

            <div className="platform-select-wrap">
              <label id="platformSelectLabel">Where are you posting?</label>
              <div className="platform-pills" role="group" aria-labelledby="platformSelectLabel">
                <button className={`platform-pill${selectedPlatform === "all" ? " selected" : ""}`} data-platform="all"
                  aria-pressed={selectedPlatform === "all"} onClick={() => setSelectedPlatform("all")} data-testid="platform-pill-all">✦ All Platforms</button>
                {PLATFORM_ORDER.map((p) => (
                  <button key={p} className={`platform-pill${selectedPlatform === p ? " selected" : ""}`} data-platform={p}
                    aria-pressed={selectedPlatform === p} onClick={() => setSelectedPlatform(p)} data-testid={`platform-pill-${p}`}>
                    {PLATFORMS[p].label.replace(" / Blog", "")}
                  </button>
                ))}
              </div>
              <div className="platform-aeo-note" data-testid="platform-note">{PLATFORM_NOTES[selectedPlatform]}</div>
            </div>

            <div className="divider" />

            <div className="btn-row">
              <button className="btn btn-teal btn-full" onClick={generatePost} data-testid="generate-button">🌵 Generate Post</button>
              <button className="btn btn-o" onClick={clearForm} data-testid="clear-button">Clear</button>
              <button className="btn btn-o" onClick={() => setActiveTab("calendar")} data-testid="calendar-shortcut-button">📅 Calendar</button>
            </div>

            <div className="what-box">
              <strong>AEO tip: where to post first</strong>
              Your website and Substack do the most work for AI findability. Post there first, then adapt down to the social platforms. Every post on joannelangston.com builds your AI entity profile.
            </div>

            {hint && <div className="hint-box" data-testid="hint-box"><strong style={{ color: "var(--teal3)" }}>Effort note:</strong> {hint.replace(/^Day \d+ note: /, "")}</div>}
          </div>

          {/* RESULTS PANEL */}
          <div className="results-panel">
            <div className="tabs-row" role="tablist" aria-label="Findability Cycle sections">
              {TABS.map((t) => (
                <button key={t.key} role="tab" id={`tabbtn-${t.key}`} aria-selected={activeTab === t.key}
                  aria-controls={`tab-${t.key}`} className={`tab-btn${activeTab === t.key ? " active" : ""}`}
                  onClick={() => setActiveTab(t.key)} data-testid={`tab-${t.key}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {activeTab === "generate" && (
              <div className="tab-pane" id="tab-generate" role="tabpanel" aria-labelledby="tabbtn-generate">
                <div className="bridge-card">
                  <div className="bridge-title">Start here if you're new to your Findability Cycle</div>
                  <div className="bridge-desc">Fill in your details, choose your effort, choose your platform, and hit Generate. Every post is already formatted for where you're posting.</div>
                  <div className="bridge-step"><div className="bridge-num">1</div><span>Paste your Searchable Title and AEO-Ready Bio from your Findability Session</span></div>
                  <div className="bridge-step"><div className="bridge-num">2</div><span>Choose an effort and select where you are posting — or choose All Platforms</span></div>
                  <div className="bridge-step"><div className="bridge-num">3</div><span>Copy the version for each platform, post it, and mark the effort done</span></div>
                </div>
                {renderCard()}
              </div>
            )}

            {activeTab === "calendar" && (
              <div className="tab-pane" id="tab-calendar" role="tabpanel" aria-labelledby="tabbtn-calendar">
                <h2 className="section-h2">Your Effort Calendar</h2>
                <p className="section-sub">Select an effort to open it. Mark it done when you have completed the effort.</p>
                <div className="cal-grid" data-testid="calendar-grid">
                  {ALL_DAYS.map((d) => {
                    const done = completed.includes(d.day);
                    return (
                      <div key={d.day} className={`cal-day${done ? " done" : ""}`}
                        role="button" tabIndex={0}
                        aria-label={`Effort ${d.day}: ${d.theme}${done ? ", completed" : ", not completed"}. Open effort to draft.`}
                        onClick={() => selectEffort(d.day)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectEffort(d.day); } }}
                        style={{ cursor: "pointer", position: "relative" }}
                        data-testid={`cal-day-${d.day}`}>
                        <div className="dn">Effort {d.day}</div>
                        <div className="dt">{d.theme}</div>
                        {done && <div className="db">✓ Done</div>}
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); toggleDay(d.day); }}
                          data-testid={`cal-mark-${d.day}`}
                          aria-label={done ? `Undo completion for Effort ${d.day}` : `Mark Effort ${d.day} done`}
                          style={{ marginTop: 8, fontSize: ".72rem", fontWeight: 700, padding: "3px 10px",
                                   borderRadius: 999, cursor: "pointer",
                                   border: done ? "1px solid #b45309" : "1px solid #2f6f5f",
                                   background: done ? "rgba(180,83,9,.12)" : "rgba(47,111,95,.12)",
                                   color: done ? "#b45309" : "#2f6f5f" }}>
                          {done ? "Undo" : "Mark done"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === "vault" && (
              <div className="tab-pane" id="tab-vault" role="tabpanel" aria-labelledby="tabbtn-vault">
                <h2 className="section-h2">Saved Posts</h2>
                <p className="section-sub">Your finished posts, organized by effort and platform. Edit the text, set a status, and note an optional image reference.</p>
                <div className="vault-filters" role="tablist" aria-label="Filter saved posts by status" data-testid="vault-filters">
                  {[{ key: "all", label: "All" }, ...STATUS_OPTS].map((f) => {
                    const count = f.key === "all" ? vault.length : vault.filter((v) => (v.status || "draft") === f.key).length;
                    return (
                      <button
                        key={f.key}
                        className={`vfilter${vaultFilter === f.key ? " active" : ""}`}
                        onClick={() => setVaultFilter(f.key)}
                        aria-pressed={vaultFilter === f.key}
                        data-testid={`vault-filter-${f.key}`}
                      >
                        {f.label} ({count})
                      </button>
                    );
                  })}
                </div>
                <div data-testid="vault-list">
                  {vault.length === 0 ? (
                    <div className="empty-state" data-testid="vault-empty-state">No posts saved yet. Generate a post, fill in your details in the card, and click Save to store it here.</div>
                  ) : (() => {
                    const filtered = vault.filter((v) => vaultFilter === "all" || (v.status || "draft") === vaultFilter);
                    if (filtered.length === 0) {
                      return <div className="empty-state" data-testid="vault-filter-empty">No {statusLabel(vaultFilter)} posts yet.</div>;
                    }
                    return filtered.map((item) => {
                      const tagCls = TYPE_TAGS[item.type] || "tag-ai";
                      const tagLbl = TYPE_LABELS[item.type] || "";
                      const status = item.status || "draft";
                      const edit = vaultEdits[item.id] || {};
                      const displayText = toCustomerText(item.text);   // transform legacy raw PP markers on render
                      const textVal = edit.text ?? displayText;
                      const imgVal = edit.imageRef ?? (item.imageRef || "");
                      const dirty = textVal !== displayText || imgVal !== (item.imageRef || "");
                      return (
                        <div className="vault-item vault-item-full" key={item.id} data-testid={`vault-item-${item.id}`}>
                          <div className="vault-head">
                            <div>
                              <div className="vault-name">Effort {item.day} — {item.platformLabel || ""}</div>
                              <div className="vault-meta">
                                {item.theme} · {item.saved} · <span className={`rb-tag ${tagCls}`} style={{ fontSize: ".58rem", padding: "2px 5px" }}>{tagLbl}</span>
                              </div>
                            </div>
                            <span className={`status-badge status-${status}`} data-testid={`vault-status-badge-${item.id}`}>{statusLabel(status)}</span>
                          </div>
                          <textarea
                            className="vault-edit-area"
                            data-testid={`vault-text-${item.id}`}
                            value={textVal}
                            rows={8}
                            onChange={(e) => setVaultEdits((m) => ({ ...m, [item.id]: { ...m[item.id], text: e.target.value } }))}
                          />
                          {countPrompts(textVal) > 0 && (
                            <div className="pp-needs-input" data-testid={`vault-pp-needs-input-${item.id}`}
                              style={{ marginTop: 6, padding: "6px 10px", borderRadius: 8, fontSize: ".78rem",
                                       background: "rgba(217,119,6,.10)", border: "1px solid rgba(217,119,6,.35)", color: "#b45309" }}>
                              ✍️ {countPrompts(textVal)} spot{countPrompts(textVal) > 1 ? "s" : ""} still need your input.
                            </div>
                          )}
                          <div className="vault-field-row">
                            <label className="vault-field">
                              <span>Image reference (optional)</span>
                              <input
                                type="text"
                                placeholder="URL or filename — e.g. canva-day1-hero.png"
                                data-testid={`vault-imageref-${item.id}`}
                                value={imgVal}
                                onChange={(e) => setVaultEdits((m) => ({ ...m, [item.id]: { ...m[item.id], imageRef: e.target.value } }))}
                              />
                            </label>
                            <label className="vault-field vault-field-status">
                              <span>Status</span>
                              <select
                                value={status}
                                data-testid={`vault-status-${item.id}`}
                                onChange={(e) => patchVault(item.id, { status: e.target.value })}
                              >
                                {STATUS_OPTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                              </select>
                            </label>
                          </div>
                          <div className="vault-actions">
                            <button
                              className="btn-tiny"
                              disabled={!dirty}
                              onClick={() => patchVault(item.id, { text: textVal, imageRef: imgVal })}
                              data-testid={`vault-save-${item.id}`}
                            >
                              {dirty ? "Save changes" : "Saved"}
                            </button>
                            <button className="btn-tiny" onClick={() => copyText(textVal, `vault_${item.id}`)} data-testid={`vault-copy-${item.id}`}>
                              {copiedKey === `vault_${item.id}` ? "Copied!" : "Copy"}
                            </button>
                            <button className="btn-tiny" onClick={() => removeVault(item.id)} data-testid={`vault-delete-${item.id}`}>✕ Delete</button>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {activeTab === "findability" && (
              cycleMode
                ? <CycleFindabilityPanel />
                : <FindabilityCheck hasTitle={!!(form.searchableTitle || "").trim()} />
            )}

            {activeTab === "history" && <PastCycles />}

            {activeTab === "platforms" && (
              <div className="tab-pane" id="tab-platforms" role="tabpanel" aria-labelledby="tabbtn-platforms">
                <h2 className="section-h2">Where AI Goes to Find You</h2>
                <p className="section-sub">Not all platforms are equal for AEO. Here is how to think about each one.</p>
                <div className="platform-guide">
                  {PLATFORM_GUIDE.map((g) => (
                    <div className="pg-card" key={g.name}>
                      <div className="pg-name">{g.name} <span className={`pg-aeo ${g.cls}`}>{g.aeo}</span></div>
                      <div className="pg-desc">{g.desc}</div>
                      <div className="pg-tip">{g.tip}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "tips" && (
              <div className="tab-pane" id="tab-tips" role="tabpanel" aria-labelledby="tabbtn-tips">
                <h2 className="section-h2">How AI Decides to Cite You</h2>
                <p className="section-sub">These are the patterns that make AI tools pull your name into answers.</p>
                <div className="tips-grid">
                  {AEO_TIPS.map((t) => (
                    <div className="tip-card" key={t.title}>
                      <div className="tip-icon">{t.icon}</div>
                      <div className="tip-title">{t.title}</div>
                      <div className="tip-desc">{t.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="lcs-footer">
        <span>🌵 Lonesome Cactus Studio · Visual Story Development · Las Vegas, Nevada</span>
        <span><a href="https://joannelangston.com" target="_blank" rel="noreferrer">joannelangston.com</a></span>
      </div>

      {showDay30 && (
        <Day30Celebration stats={stats} vault={vault} onClose={() => setShowDay30(false)} />
      )}
    </>
  );
}
